import { useMemo } from 'react';
import { useAuth } from '../core/auth/AuthContext';
import {
  resolvePermission,
  hasAccessLevel,
  hasReadLevel,
  hasWriteLevel,
  findPermissionForPath,
  type PermissionLevel,
} from '@dental-manager/shared';

export function usePermissions() {
  const { user, permissions, isOwner } = useAuth();

  return useMemo(() => {
    const role = user?.role || 'employee';

    const getEffective = (key: string): PermissionLevel => {
      return resolvePermission(role, key, permissions, isOwner);
    };

    const hasAccess = (key: string): boolean => {
      return hasAccessLevel(getEffective(key));
    };

    const canRead = (key: string): boolean => {
      return hasReadLevel(getEffective(key));
    };

    const canWrite = (key: string): boolean => {
      return hasWriteLevel(getEffective(key));
    };

    const hasPathAccess = (pathname: string): boolean => {
      const permKey = findPermissionForPath(pathname);
      if (!permKey) return true; // no permission mapped = always allow
      return hasAccess(permKey);
    };

    return {
      hasAccess,
      canRead,
      canWrite,
      hasPathAccess,
      getEffective,
      isOwner,
    };
  }, [user?.role, permissions, isOwner]);
}
